import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CropFreeOutlinedIcon from '@mui/icons-material/CropFreeOutlined';

import { useMapEditor } from '../MapEditorContext';
import { useMapEditorCatalog } from '../useMapEditorCatalog';
import { findCatalogElement } from '../mapEditorCatalog';
import { describeRefusalSummary } from '../mapEditorRefusalText';
import { useInspectorStyles } from './styles';

/**
 * The Layout panel: what the walk is, and what to do next with it.
 *
 * Almost everything that used to be here has moved, and the moves are the redesign:
 *
 *  - the **segment list** went into the Scene tree, where every other thing in the scene is already
 *    listed one by one (see LayoutTree);
 *  - **Publish**, **Undo all** and the draft/live status went onto a plaque under that tree, because
 *    they are about the whole layout (see LayoutPlaque);
 *  - everything about one segment — its picture, its name, **Change**, **Take me there**, its frames
 *    and its surfaces — went into the segment panel (see SegmentInspector);
 *  - **Start over** and the `From above` / `Walk it` toggle were removed outright. Do not re-add
 *    them; the reasons are on LayoutPlaque.
 *
 * What is left is what has nowhere else to be: the one sentence about a refusal, which is about the
 * chain rather than about any single segment, and the line that says where to go next. This panel is
 * what the author sees before they have selected anything, so it has to answer "what now".
 */
const MapEditorInspector = () => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const { rooms, refusals, previewRefusals, isDirty } = useMapEditor();
  const { elements: catalogElements } = useMapEditorCatalog();

  // A dirty draft is described by the draft's own report and only by that one: the published report
  // was keyed against a different chain, and the author's edits have moved every row out from under
  // it. Once the draft matches what is standing, the published report is the better answer — it
  // describes the world every participant is in rather than one browser's preview.
  const activeRefusals = isDirty ? previewRefusals : refusals;

  const refusalSummary = describeRefusalSummary(
    activeRefusals,
    (id: string) => findCatalogElement(catalogElements, id)?.displayName ?? id,
    translate,
    rooms,
  );

  return (
    <Box data-testid="map-editor-layout-inspector">
      {/* First, because it is the answer to "why is my layout shorter than my list". The per-row
          markers in the tree say which segment; this says what happened to the chain. */}
      {!!refusalSummary && (
        <Box
          sx={styles.mapEditorRefusedBanner}
          data-testid="map-editor-refused-banner"
        >
          <BlockOutlinedIcon sx={styles.mapEditorRefusedIcon} />
          <Typography sx={styles.mapEditorRefusedText}>
            {refusalSummary}
          </Typography>
        </Box>
      )}

      <Box sx={styles.mapEditorEmpty}>
        <CropFreeOutlinedIcon sx={styles.mapEditorEmptyIcon} />
        <Typography sx={styles.mapEditorEmptyTitle}>
          {translate('editorMode.mapEditorNothingSelected', 'Nothing selected')}
        </Typography>
        <Typography sx={styles.emptyHint}>
          {rooms.length
            ? translate(
                'editorMode.mapEditorPickASegment',
                'Pick a segment on the left to work on it — its picture, what is on its walls, and its surfaces.',
              )
            : translate(
                'editorMode.mapEditorAddASegment',
                'Add a segment on the left, or drop in a whole walk from one of the starters, and it appears here.',
              )}
        </Typography>
      </Box>
    </Box>
  );
};

export default MapEditorInspector;
